import type { DialogStore } from "@ariakit/react";
import type { ComponentPropsWithRef, PropsWithChildren } from "react";

import {
  Dialog as ReactDialog,
  DialogHeading,
  DialogProvider,
} from "@ariakit/react";
import { AnimatePresence, m } from "motion/react";

import { Button } from "~/common/components/button";
import { dialogStyle } from "~/common/components/dialog.style";
import { IconClose } from "~/common/components/icons/close";

interface DialogProps {
  id: string;
  store: DialogStore;
  heading: string;
  showCancelButton?: boolean;
  buttonStyle?: "default" | "full";
  submitButtonLabel?: string;
  disableSubmitButton?: boolean;
}

export function Dialog({
  id,
  store,
  heading,
  showCancelButton = true,
  buttonStyle = "default",
  submitButtonLabel = "Done",
  disableSubmitButton,
  children,
}: PropsWithChildren<DialogProps>) {
  function handleClose() {
    store.setOpen(false);
  }

  return (
    <AnimatePresence>
      <DialogProvider store={store}>
        <ReactDialog
          modal
          autoFocusOnShow={false}
          key={id}
          portal
          backdrop={<DialogBackdrop />}
          className={dialogStyle.dialog()}
        >
          <m.div
            exit={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 30 }}
            className={dialogStyle.container()}
          >
            <div className={dialogStyle.header()}>
              <DialogHeading className={dialogStyle.heading()}>
                {heading}
              </DialogHeading>
              <Button
                variant="secondary"
                className={dialogStyle.closeButton()}
                onClick={handleClose}
              >
                <IconClose color="currentColor" />
              </Button>
            </div>

            <div className={dialogStyle.content()}>{children}</div>

            <div className={dialogStyle.footer()}>
              {!!showCancelButton && (
                <Button
                  className={dialogStyle.button({ variant: buttonStyle })}
                  background="on-white"
                  variant="ghost"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              )}
              <Button
                className={dialogStyle.button({ variant: buttonStyle })}
                background="on-white"
                onClick={handleClose}
                disabled={disableSubmitButton}
              >
                {submitButtonLabel}
              </Button>
            </div>
          </m.div>
        </ReactDialog>
      </DialogProvider>
    </AnimatePresence>
  );
}

function DialogBackdrop(props: ComponentPropsWithRef<"div">) {
  return (
    <div {...props}>
      <m.div
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="pointer-events-none size-full touch-none backdrop-brightness-50"
      />
    </div>
  );
}
